import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { Col, Dropdown, Icon, Menu, Row, Table, notification } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Creators as SupplementActions } from "../../../store/ducks/supplement";
import {
  activateSupplement,
  getSupplementPageableIndex,
  inactivateSupplement,
} from "../../../services/supplementService";
import ButtonStandard from "../../../components/utils/button";
import MenuIcon from "../../../components/utils/table/icons/menu";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import TagStatus from "../../../components/utils/tagStatus";
import { transformCamelCaseToSnakeCase } from "../../../utils/stringUtils";
import {
  getColumnDateSearchProps,
  getColumnSearchProps,
} from "../../../utils/tableUtils";

const Column = Table.Column;

const SupplementGroupTable = ({
  isJustRawMaterials,
  groupFilterExpression,
  createButtonLabel,
  defaultSupplementGroupId,
  showGroupColumn,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [tableFilters, setTableFilters] = useState(null);
  const [tableSorters, setTableSorters] = useState(null);

  const dispatch = useDispatch();

  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
    user: { isInputSupplier },
  } = useSelector((state) => state.app);

  const getSortString = useCallback((sorter) => {
    let customSort = sorter;

    if (sorter && Object.entries(sorter).length > 0) {
      const { field } = sorter;
      const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
      customSort = { ...customSort, field: fieldS };
    }

    return customSort;
  }, []);

  const getSearchString = useCallback((filters) => {
    let search = "";
    if (filters && Object.entries(filters).length > 0) {
      Object.entries(filters).forEach(([field, value]) => {
        if (value && value.length > 0) {
          const fieldS = field ? transformCamelCaseToSnakeCase(field) : null;
          if (fieldS === "status") {
            if (value.includes("I")) value.push("X");
            search =
              search === ""
                ? `${fieldS} in (${value.map((o) => (o = `'${o}'`)).join(", ")})`
                : `${search};${fieldS} in (${value
                    .map((o) => (o = `'${o}'`))
                    .join(", ")})`;
          } else if (fieldS === "created_at") {
            const operatorS =
              value[1] === "equal"
                ? "="
                : value[1] === "greaterThan"
                  ? ">"
                  : value[1] === "greaterOrEqualThan"
                    ? ">="
                    : value[1] === "lessThan"
                      ? "<"
                      : value[1] === "lessOrEqualThan"
                        ? "<="
                        : "=";

            search =
              search === ""
                ? `cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`
                : `${search};cast(${fieldS} as date) ${operatorS} cast('${moment(
                    value[0],
                  ).format("YYYY-MM-DD")}' as date)`;
          } else {
            search =
              search === ""
                ? `upper(${fieldS}) like upper('%25${value}%25')`
                : `${search};upper(${fieldS}) like upper('%25${value}%25')`;
          }
        }
      });
    }

    return search !== "" ? search : "";
  }, []);

  const fetchData = useCallback(
    async (
      pageToFetch = 0,
      sizeToFetch = 10,
      sorter = {},
      filters = "",
      newTableFilters = null,
      newTableSorters = null,
    ) => {
      setIsLoading(true);
      try {
        const combinedFilters = [groupFilterExpression, filters]
          .filter((expression) => expression && expression !== "")
          .join(";");
        const {
          data: { results },
        } = await getSupplementPageableIndex({
          groupId,
          farmId,
          filters: combinedFilters,
          page: pageToFetch,
          size: sizeToFetch,
          sorter,
          isInputSupplier,
        });
        setPageData(results);
        setPage(pageToFetch);
        setSize(sizeToFetch);
        setTableFilters(newTableFilters);
        setTableSorters(newTableSorters);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [groupId, farmId, groupFilterExpression, isInputSupplier],
  );

  useEffect(() => {
    if (groupId && farmId) {
      fetchData();
    }
  }, [groupId, farmId, fetchData]);

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = (clearFilters) => {
    clearFilters();
  };

  const handleTableChange = useCallback(
    async (pagination = null, filters = null, sorter = null) => {
      if (pagination && filters && sorter) {
        const customSort = getSortString(sorter);
        const search = getSearchString(filters);
        await fetchData(
          pagination.current,
          pagination.pageSize,
          customSort,
          search,
          filters,
          sorter,
        );
      } else {
        await fetchData(0, 10, {}, "", filters, sorter);
      }
    },
    [getSortString, getSearchString, fetchData],
  );

  const handleReload = useCallback(() => {
    handleTableChange(
      { current: page, pageSize: size },
      tableFilters,
      tableSorters,
    );
  }, [handleTableChange, page, size, tableFilters, tableSorters]);

  const handleNew = useCallback(() => {
    dispatch(
      SupplementActions.showDrawer("new", null, defaultSupplementGroupId),
    );
  }, [dispatch, defaultSupplementGroupId]);

  const handleNewSupplementFormulated = useCallback(() => {
    dispatch(SupplementActions.showHideCreateEditSupplementFormulated());
  }, [dispatch]);

  const handleEdit = useCallback(
    (id, supplementFormulated) => {
      if (supplementFormulated) {
        dispatch(SupplementActions.showHideCreateEditSupplementFormulated(id));
      } else {
        dispatch(SupplementActions.showDrawer("edit", id));
      }
    },
    [dispatch],
  );

  const handleDetails = useCallback(
    (id) => {
      dispatch(SupplementActions.showHideSupplementDetails(id));
    },
    [dispatch],
  );

  const handleInactivateSupplement = useCallback(
    async (id) => {
      try {
        await inactivateSupplement({ groupId, farmId, id });
        await handleReload();
        notification.success({
          message: translation.supplement.successInactivate,
        });
      } catch (error) {
        console.error("Failed to inactivate supplement:", error);
        notification.error({ message: translation.supplement.errorInactivate });
      }
    },
    [groupId, farmId, handleReload, translation],
  );

  const handleActivateSupplement = useCallback(
    async (id) => {
      try {
        await activateSupplement({ groupId, farmId, id });
        await handleReload();
        notification.success({
          message: translation.supplement.successActivate,
        });
      } catch (error) {
        console.error("Failed to activate supplement:", error);
        notification.error({ message: translation.supplement.errorActivate });
      }
    },
    [groupId, farmId, handleReload, translation],
  );

  const menu = (record) => (
    <Menu>
      {record.status !== "F" &&
        record.status !== "Bovexo" &&
        record.status !== "I" &&
        record.status !== "Inactive" && (
          <Menu.Item
            key="1"
            onClick={() =>
              handleEdit(record.id, record.supplementFormulated)
            }
          >
            {translation.table.menu.edit}
          </Menu.Item>
        )}
      <Menu.Item key="2" onClick={() => handleDetails(record.id)}>
        {translation.table.menu.details}
      </Menu.Item>
      {(record.status === "A" || record.status === "Active") && (
        <Menu.Item
          key="3"
          onClick={() => handleInactivateSupplement(record.id)}
        >
          {translation.table.menu.inactivate}
        </Menu.Item>
      )}
      {(record.status === "I" || record.status === "Inactive") && (
        <Menu.Item key="4" onClick={() => handleActivateSupplement(record.id)}>
          {translation.table.menu.activate}
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <>
      <Row type="flex" justify="end" gutter={8} style={{ marginBottom: 16 }}>
        <Col>
          <ButtonStandard
            buttonType="typeWithoutBackground"
            onClick={handleReload}
          >
            <Icon type="reload" />
          </ButtonStandard>
        </Col>
        {!isJustRawMaterials && (
          <Col>
            <ButtonStandard
              type="button"
              buttonType="addNew"
              onClick={handleNewSupplementFormulated}
            >
              {translation.supplement.buttonNewSupplementFormulated}
            </ButtonStandard>
          </Col>
        )}
        <Col>
          <ButtonStandard type="button" buttonType="addNew" onClick={handleNew}>
            {createButtonLabel}
          </ButtonStandard>
        </Col>
      </Row>
      <Table
        loading={isLoading}
        rowKey="id"
        size="small"
        dataSource={pageData != null ? pageData.content : []}
        pagination={{
          total: pageData != null ? pageData.totalElements : 0,
          size,
          current: page,
          hideOnSinglePage:
            pageData !== null && Object.entries(pageData).length !== 0
              ? pageData.totalElements > 10
                ? false
                : true
              : true,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "30", "100", "200"],
        }}
        scroll={{ x: true }}
        onChange={handleTableChange}
        style={{ width: "100%", margin: "0" }}
      >
        <Column
          title={translation.supplement.table.columns.status}
          dataIndex="status"
          key="status"
          align="left"
          filtered
          filters={[
            { text: translation.status.active, value: "A" },
            { text: translation.status.bovexo, value: "F" },
            { text: translation.status.inactive, value: "I" },
            { text: translation.status.pending, value: "P" },
          ]}
          filterMultiple
          filteredValue={tableFilters?.status || null}
          sorter
          sortOrder={tableSorters?.columnKey === "status" && tableSorters.order}
          render={(status) =>
            status === "A" || status === "Active" ? (
              <TagStatus
                background="#C5F1CA"
                borderColor="#106518"
                color="#106518"
              >
                {translation.status.active}
              </TagStatus>
            ) : status === "F" || status === "Bovexo" ? (
              <TagStatus
                background="#F6FFC8"
                borderColor="#A9C133"
                color="#A9C133"
              >
                Bovexo
              </TagStatus>
            ) : status === "P" || status === "Pending" ? (
              <TagStatus
                background="#FFDBBC"
                borderColor="#FE8D2A"
                color="#FE8D2A"
              >
                {translation.status.pending}
              </TagStatus>
            ) : (
              <TagStatus
                background="#FBC7C7"
                borderColor="#D44C4C"
                color="#D44C4C"
              >
                {translation.status.inactive}
              </TagStatus>
            )
          }
        />
        <Column
          title={translation.supplement.table.columns.description}
          dataIndex="name"
          align="left"
          key="name"
          {...getColumnSearchProps("name", handleReset, handleSearch)}
          filteredValue={tableFilters?.name || null}
          sorter
          sortOrder={tableSorters?.columnKey === "name" && tableSorters.order}
        />
        <Column
          title={translation.supplement.table.columns.industrialName}
          dataIndex="industrialName"
          align="left"
          key="industrialName"
          {...getColumnSearchProps("industrialName", handleReset, handleSearch)}
          filteredValue={tableFilters?.industrialName || null}
          sorter
          sortOrder={
            tableSorters?.columnKey === "industrialName" && tableSorters.order
          }
        />
        {showGroupColumn && (
          <Column
            title={translation.supplement.table.columns.supplementGroupName}
            dataIndex="supplementGroupName"
            key="supplementGroupName"
            align="left"
            {...getColumnSearchProps(
              "supplementGroupName",
              handleReset,
              handleSearch,
            )}
            filteredValue={tableFilters?.supplementGroupName || null}
            sorter
            sortOrder={
              tableSorters?.columnKey === "supplementGroupName" &&
              tableSorters.order
            }
          />
        )}
        <Column
          title={translation.supplement.table.columns.supplementSubGroupName}
          dataIndex="supplementSubGroupName"
          key="supplementSubGroupName"
          align="left"
          {...getColumnSearchProps(
            "supplementSubGroupName",
            handleReset,
            handleSearch,
          )}
          filteredValue={tableFilters?.supplementSubGroupName || null}
          sorter
          sortOrder={
            tableSorters?.columnKey === "supplementSubGroupName" &&
            tableSorters.order
          }
        />
        <Column
          title={
            translation.supplement.table.columns.supplementPresentationName
          }
          dataIndex="supplementPresentationName"
          key="supplementPresentationName"
          align="left"
          {...getColumnSearchProps(
            "supplementPresentationName",
            handleReset,
            handleSearch,
          )}
          filteredValue={tableFilters?.supplementPresentationName || null}
          sorter
          sortOrder={
            tableSorters?.columnKey === "supplementPresentationName" &&
            tableSorters.order
          }
        />
        <Column
          title={translation.supplement.table.columns.supplierName}
          dataIndex="supplierName"
          align="left"
          key="supplierName"
          {...getColumnSearchProps("supplierName", handleReset, handleSearch)}
          filteredValue={tableFilters?.supplierName || null}
          sorter
          sortOrder={
            tableSorters?.columnKey === "supplierName" && tableSorters.order
          }
        />
        <Column
          title={translation.supplement.table.columns.createdAt}
          dataIndex="createdAt"
          align="left"
          key="createdAt"
          {...getColumnDateSearchProps("createdAt", handleReset, handleSearch)}
          filteredValue={tableFilters?.createdAt || null}
          sorter
          sortOrder={
            tableSorters?.columnKey === "createdAt" && tableSorters.order
          }
          render={(text, record) => (
            <span>{moment(record.createdAt).format("DD/MM/YYYY")}</span>
          )}
        />
        <Column
          align="left"
          render={(text, record) => (
            <Dropdown
              overlay={menu(record)}
              trigger={["click"]}
              key={record.id}
            >
              <MenuContainer>
                <MenuIcon />
              </MenuContainer>
            </Dropdown>
          )}
        />
      </Table>
    </>
  );
};

export default SupplementGroupTable;
